/**
 * CircuitSimulator.js
 * Embeds CircuitJS (Falstad) for full circuit simulation.
 * Users access pre-built circuits via the simulator's built-in File menu.
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
} from '@mui/material';
import {
  OpenInNew,
  Close,
  Fullscreen,
  FullscreenExit,
  HelpOutline,
} from '@mui/icons-material';

const FALSTAD_URL = 'https://www.falstad.com/circuit/circuitjs.html';

const INSTRUCTIONS = [
  {
    step: '1',
    heading: 'Open a pre-built circuit',
    detail: 'Click the Circuits menu inside the simulator to browse the built-in library — logic gates, amplifiers, oscillators, filters, and more.',
  },
  {
    step: '2',
    heading: 'Interact with the circuit',
    detail: 'Click any switch or button to toggle it. Hover over a wire or component to see live voltage and current readings.',
  },
  {
    step: '3',
    heading: 'Draw your own circuit',
    detail: 'Right-click on the canvas to add components. Drag wires between nodes to connect them.',
  },
  {
    step: '4',
    heading: 'Adjust simulation speed',
    detail: 'Use the Speed slider (top-right of the simulator) to slow down or speed up — great for watching flip-flops step by step.',
  },
  {
    step: '5',
    heading: 'Open externally for more space',
    detail: 'Click the ↗ button in the toolbar above to open Falstad in a full browser tab with all menus fully visible.',
  },
];

function CircuitSimulator({
  open,
  onClose,
  title = 'Circuit Lab',
  dialogProps = {},
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isFullscreen}
      PaperProps={{
        sx: {
          height: isFullscreen ? '100%' : '90vh',
          maxHeight: isFullscreen ? '100%' : '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      {...dialogProps}
    >
      {/* Title bar */}
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.25, px: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>⚡ {title}</Typography>
          <Chip label="Falstad CircuitJS" size="small" color="success" sx={{ fontSize: '0.7rem' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setShowHelp(v => !v)} title={showHelp ? 'Hide instructions' : 'Show instructions'}>
            <HelpOutline fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => window.open(FALSTAD_URL, '_blank', 'noopener,noreferrer')} title="Open in new tab">
            <OpenInNew fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setIsFullscreen(v => !v)} title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
            {isFullscreen ? <FullscreenExit fontSize="small" /> : <Fullscreen fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={onClose} title="Close">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Body: iframe left, instructions right */}
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'row',
          overflow: 'hidden',
        }}
      >
        {/* Simulator iframe */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <iframe
            src={FALSTAD_URL}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            title="Falstad Circuit Simulator"
            allow="fullscreen"
          />
        </Box>

        {/* Right-side instruction panel */}
        {showHelp && (
          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              borderLeft: '1px solid',
              borderColor: 'divider',
              overflowY: 'auto',
              bgcolor: 'background.default',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
              sx={{ letterSpacing: 1, textTransform: 'uppercase', px: 2, pt: 2, pb: 1, display: 'block' }}
            >
              How to use
            </Typography>

            {INSTRUCTIONS.map((item) => (
              <Box
                key={item.step}
                sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}
              >
                <Box
                  sx={{
                    minWidth: 22, height: 22, borderRadius: '50%',
                    bgcolor: 'success.main', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.68rem', fontWeight: 700, mt: 0.2, flexShrink: 0,
                  }}
                >
                  {item.step}
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ lineHeight: 1.4 }}>
                    {item.heading}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.5, mt: 0.25 }}>
                    {item.detail}
                  </Typography>
                </Box>
              </Box>
            ))}

            <Box sx={{ mt: 'auto', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.disabled" sx={{ lineHeight: 1.5, display: 'block' }}>
                Powered by CircuitJS<br />by Paul Falstad
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default CircuitSimulator;
